import { ViewportScroller } from '@angular/common';
import { Component, DOCUMENT, HostListener, Inject, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, Scroll } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MatomoTracker } from 'ngx-matomo-client';
import { filter, tap } from 'rxjs/operators';

import { faIcons } from 'app/core/app-constants/used-fa-icons';
import { AuthenticationService } from 'app/core/authentication';
import { ACCESS_ROUTE } from 'app/access/access-routing.constant';
import { TOKEN_STORAGE_KEYS } from 'app/core/authentication/constants/token-storage-keys';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { TranslationLoaderHelper } from 'app/core/internationalization/translation-loader-helper';
import { RouterService } from 'app/core/navigation';
import { ThemesService } from 'app/core/themes/themes.service';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';
import { SingleSignOnService } from 'app/shared/services/single-sign-on.service';
import { environment } from 'env/environment';
import { LOCALE_CONFIG } from 'env/locale-config';

@UntilDestroy()
@Component({
  selector: 'app-root',
  styleUrls: ['./app.component.less'],
  templateUrl: './app.component.html',
  standalone: false
})

export class AppComponent implements OnInit {
  public title = 'Web App';
  public themeUrl?: SafeResourceUrl;
  public translationsLoading$ = this.translationLoader.loading$;

  public constructor(
    private readonly tracker: MatomoTracker,
    private readonly translationService: TranslationService,
    private readonly translationLoader: TranslationLoaderHelper,
    private readonly sanitizer: DomSanitizer,
    private readonly themesService: ThemesService,
    private readonly authenticationService: AuthenticationService,
    private readonly singleSign: SingleSignOnService,
    private readonly routerService: RouterService,
    @Inject(DOCUMENT) private document: Document,
    library: FaIconLibrary,
    private readonly router: Router,
    viewportScroller: ViewportScroller,
    public oidcSecurityService: OidcSecurityService,
    public khAnalyticsService: KhAnalyticsService) {
    this.authenticationService.isAuthorized()
      .pipe(tap(value => {
        let locale = '';
        if (value) {
          locale = this.authenticationService.getUserClaims().localeId;
        } else {
          const item = sessionStorage.getItem(TranslationService.SESSION_LANGUAGE_KEY);
          locale = item ? JSON.parse(item).LocaleId : LOCALE_CONFIG.APP_LOCALE;
        }
        this.translationService.loadLanguage(locale);
      }))
      .subscribe();

    library.addIcons(...faIcons);

    router.events
      .pipe(
        filter((event): event is Scroll => event instanceof Scroll),
        untilDestroyed(this))
      .subscribe(e => {
        if (!router.currentNavigation()?.extras?.state?.suppressScroll) {
          viewportScroller.scrollToPosition(e.position ?? [0, 0]);
        }
      });
  }

  @HostListener('window:unload', ['$event']) protected handleUnload() {
    this.khAnalyticsService.flushEvents();
  }

  public ngOnInit(): void {
    this.authenticationService.userSloRedirect()
      .pipe(
        tap(() => {
          const fullUrl = environment.AngularJsBaseUrl + ACCESS_ROUTE.KH_SAML_LOGOUT_REDIRECT.route().join('/');

          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = fullUrl;

          iframe.onload = iframe.onerror = () => {
            setTimeout(() => { iframe.remove(); }, 5000);
          };

          document.body.appendChild(iframe);
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.authenticationService.isAuthorized()
      .pipe(
        tap(() => { this.setupMatomoUser(); }),
        untilDestroyed(this)
      )
      .subscribe();

    this.document.documentElement.lang = LOCALE_CONFIG.APP_LOCALE.split('-')[0];

    this.themesService.themeChanged()
      .pipe(
        tap(theme => this.themeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(theme)),
        untilDestroyed(this)
      )
      .subscribe();

    this.authenticationService.userLoggedIn()
      .pipe(
        tap(() => { sessionStorage.removeItem('MilestonesModalShown'); }),
        tap(() => { this.setupMatomoUser(); }),
        untilDestroyed(this)
      )
      .subscribe();

    this.authenticationService.userLoggedOut()
      .pipe(
        tap(() => {
          this.resetMatomoUser();
          this.singleSign.logoutAll();
          const postLogoutRedirectString = localStorage.getItem(TOKEN_STORAGE_KEYS.POST_LOGOUT_REDIRECT);
          if (postLogoutRedirectString) {
            const postLogoutRedirect = JSON.parse(postLogoutRedirectString);
            localStorage.removeItem(TOKEN_STORAGE_KEYS.POST_LOGOUT_REDIRECT);
            this.router.navigate(postLogoutRedirect.url, {
              queryParams: postLogoutRedirect.params
            });
          } else {
            this.routerService.navigate(ACCESS_ROUTE.route());
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.oidcSecurityService.checkAuth(environment.mceClientId).subscribe(({ isAuthenticated }) => { });
  }

  private setupMatomoUser() {
    const claims = this.authenticationService.getUserClaims();
    if (!claims) { return; }

    this.tracker.setUserId(claims.userId);
    const roles = this.authenticationService.roles().join(',');
    const roleDimesions = ['Student', 'Parent', 'Teacher'];
    for (let i = 0; i < roleDimesions.length; i++) {
      if (roles.includes(roleDimesions[i])) {
        this.tracker.setCustomDimension((i + 1), roleDimesions[i]);
      }
    }
  }

  private resetMatomoUser() {
    this.tracker.resetUserId();
    this.tracker.deleteCustomDimension(1);
    this.tracker.deleteCustomDimension(2);
    this.tracker.deleteCustomDimension(3);
  }
}
